import { useAuth } from '@/lib/store/auth';

const CONTAINER_STORAGE_KEY = 'lumeseek_quiz_container_v1';
const LEGACY_QUIZ_AUTH_STORAGE_KEYS = ['lumeseek_quiz_auth_v2', 'lumeseek_quiz_auth_v1'] as const;
const WX_TOKEN_SLOT = '__lumeseekWxToken';
const QUIZ_PUBLIC_ID = /^[A-Za-z0-9_-]{1,200}$/;
const MINI_PROGRAM_NAVIGATION_TIMEOUT_MS = 3_000;

let containerMode = typeof window !== 'undefined'
  && window.sessionStorage.getItem(CONTAINER_STORAGE_KEY) === 'miniprogram';

type MiniProgramWindow = Window & {
  wx?: { miniProgram?: {
    navigateBack?: (options: { delta: number }) => void;
    navigateTo?: (options: {
      url: string;
      success?: () => void;
      fail?: () => void;
    }) => void;
  } };
};

type InjectedTokenWindow = Window & {
  __lumeseekWxToken?: unknown;
};

export async function bootstrapQuizWebViewSession(): Promise<{
  container: 'browser' | 'miniprogram';
  status: 'not-requested' | 'authenticated' | 'failed';
}> {
  for (const key of LEGACY_QUIZ_AUTH_STORAGE_KEYS) {
    window.localStorage.removeItem(key);
  }

  const tokenSlot = window as InjectedTokenWindow;
  const tokenRequested = Object.prototype.hasOwnProperty.call(tokenSlot, WX_TOKEN_SLOT);
  const injectedToken = tokenRequested ? tokenSlot.__lumeseekWxToken : undefined;
  delete tokenSlot.__lumeseekWxToken;

  if (!tokenRequested) {
    containerMode = window.sessionStorage.getItem(CONTAINER_STORAGE_KEY) === 'miniprogram';
    return { container: containerMode ? 'miniprogram' : 'browser', status: 'not-requested' };
  }

  containerMode = true;
  window.sessionStorage.setItem(CONTAINER_STORAGE_KEY, 'miniprogram');
  if (typeof injectedToken !== 'string' || !injectedToken.trim()) {
    useAuth.getState().logout();
    return { container: 'miniprogram', status: 'failed' };
  }

  useAuth.getState().setToken(injectedToken);
  return { container: 'miniprogram', status: 'authenticated' };
}

export function isMiniProgramQuizContainer() {
  return containerMode;
}

export function navigateBackToMiniProgram() {
  if (!containerMode) return false;
  const navigateBack = (window as MiniProgramWindow).wx?.miniProgram?.navigateBack;
  if (typeof navigateBack !== 'function') return false;
  navigateBack({ delta: 1 });
  return true;
}

export function navigateToMiniProgramQuizLogin(publicId: string): Promise<boolean> {
  if (!containerMode || !QUIZ_PUBLIC_ID.test(publicId)) return Promise.resolve(false);
  const navigateTo = (window as MiniProgramWindow).wx?.miniProgram?.navigateTo;
  if (typeof navigateTo !== 'function') return Promise.resolve(false);
  const returnIntent = `/pages/quiz-entry/index?publicId=${encodeURIComponent(publicId)}`;
  const url = `/pages/login/index?redirect=${encodeURIComponent(returnIntent)}&switchAccount=1`;
  return new Promise((resolve) => {
    let settled = false;
    const settle = (opened: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(opened);
    };
    const timeoutId = window.setTimeout(() => settle(false), MINI_PROGRAM_NAVIGATION_TIMEOUT_MS);
    try {
      navigateTo({
        url,
        success: () => settle(true),
        fail: () => settle(false),
      });
    } catch {
      settle(false);
    }
  });
}
